import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { Datepicker } from "@mobiscroll/react";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";

import { LoadingButton } from "../shared/loading-button";
import ai from "../../helpers/axios";
import Loader from "../loader";

const dropdowns = {
  isoCountry: { name: "isoCountry", showDefaultOptions: true, placeholder: "Select Country" },
  region: { name: "region", showDefaultOptions: true, placeholder: "Select Region" },
  subRegion: { name: "subRegion", showDefaultOptions: true, placeholder: "Select Sub Region" },
  isoRegion: { name: "isoRegion", showDefaultOptions: true, placeholder: "Select State" },
  municipality: { name: "municipality", showDefaultOptions: false, placeholder: "Select Municipality" },
};

const categoryOptions = [
  { value: "isoCountry", label: "Country" },
  { value: "region", label: "Region" },
  { value: "subRegion", label: "Sub Region" },
  { value: "isoRegion", label: "State" },
  { value: "municipality", label: "Municipality" },
  { value: "airport", label: "Airport" },
];

export const FiltersSections = {
  Dates: "dates",
  Departure: "departure",
  Arrival: "arrival",
  Combo: "combo",
};

const initialFilters = {
  [FiltersSections.Dates]: [],
  [FiltersSections.Departure]: {
    category: null,
    ...Object.fromEntries(Object.keys(dropdowns).map(key => [key, []])),
    airport: [],
  },
  [FiltersSections.Arrival]: {
    category: null,
    ...Object.fromEntries(Object.keys(dropdowns).map(key => [key, []])),
    airport: [],
  },
  [FiltersSections.Combo]: {
    category: null,
    ...Object.fromEntries(Object.keys(dropdowns).map(key => [`${key}From`, null])),
    ...Object.fromEntries(Object.keys(dropdowns).map(key => [`${key}To`, null])),
    airportFrom: null,
    airportTo: null,
    values: [],
  },
};

const initialFiltersState = {
  prefillLoading: true,
  filters: {
    ...initialFilters,
  },
  defaultOptions: Object.keys(dropdowns)
    .reduce((res, field) => ({ ...res, [field]: [] }), {}),
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "prefillDefaultOptions": {
      return {
        ...state,
        defaultOptions: Object.entries(action.value).reduce((res, [field, options]) => ({
          ...res,
          [field]: options.map(option => ({ value: option, label: option })),
        }), []),
        prefillLoading: false,
      };
    }

    default:
      break;
  }
};

export const MetricsLocationsFilters = ({ applyFilters }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialFiltersState);

  const loadPrefillData = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    try {
      let { data } = await ai.auth(token).get(`/api/airports/metrics-filters`, {
        params: {
          field: "all",
        },
      });

      if (data) {
        dispatch({
          type: "prefillDefaultOptions",
          value: data,
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: "fieldChanged",
        field: "prefillLoading",
        value: false,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    loadPrefillData();
  }, [loadPrefillData]);

  const loadDropdownOptions = useCallback((field) => async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    let { data } = await ai.auth(token).get(`/api/airports/metrics-filters`, {
      params: {
        field,
        search: inputValue,
      },
    });

    if (data?.[field]?.length >= 1) {
      return data[field].map((option) => {
        return { value: option, label: option };
      });
    } else {
      return [];
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const loadAirportOptions = useCallback(async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}`)
      .catch((error) => {
        console.error(error);
      });

    let options = [];
    if (response.data.length >= 1) {
      options = response.data.map((i) => ({
        value: i._id,
        label: i.airportName,
        code: i.code,
      }));
    }

    return options;
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleUpdateFilters = useCallback(({ section, field, value }) => {
    const isDatesRangeChange = section === FiltersSections.Dates;
    const isCategoryChange = field === "category";

    const updatedFilters = {
      ...state.filters,
    };

    if (isDatesRangeChange) {
      updatedFilters[FiltersSections.Dates] = value;
    } else if (isCategoryChange) {
      updatedFilters[section] = { ...initialFilters[section] };
      updatedFilters[section].category = value;
    } else {
      updatedFilters[section][field] = value;
    }

    dispatch({
      type: "fieldChanged",
      field: "filters",
      value: updatedFilters,
    });
  }, [state.filters]);

  const handleAddComboFilter = useCallback(() => {
    if (!state.filters[FiltersSections.Combo].category) {
      return;
    }

    const from = state.filters[FiltersSections.Combo][`${state.filters[FiltersSections.Combo].category}From`];
    const to = state.filters[FiltersSections.Combo][`${state.filters[FiltersSections.Combo].category}To`];

    if (!from || !to) {
      return;
    }

    const values = [...state.filters[FiltersSections.Combo].values];
    if (values.find(([existsFrom, existsTo]) => existsFrom.value === from.value && existsTo.value === to.value)) {
      values.push([from, to]);
    }

    dispatch({
      type: "fieldChanged",
      field: "filters",
      value: {
        ...state.filters,
        [FiltersSections.Combo]: {
          ...state.filters[FiltersSections.Combo],
          [`${state.filters[FiltersSections.Combo].category}From`]: null,
          [`${state.filters[FiltersSections.Combo].category}To`]: null,
          values: [...state.filters[FiltersSections.Combo].values, [from, to]],
        },
      },
    });
  }, [state.filters]);

  const handleDeleteComboValue = useCallback((index) => {
    dispatch({
      type: "fieldChanged",
      field: "filters",
      value: {
        ...state.filters,
        [FiltersSections.Combo]: {
          ...state.filters[FiltersSections.Combo],
          values: state.filters[FiltersSections.Combo].values.filter((_, i) => i !== index),
        },
      },
    });
  }, [state.filters]);

  const handleApplyFilters = useCallback(() => {
    applyFilters({ filters: state.filters });
  }, [applyFilters, state.filters]);

  return (
    <>
      {
        state.prefillLoading ? (
          <Box height={335} display="flex" alignItems="center">
            <Loader/>
          </Box>
        ) : (
          <Box mt={2} pt={2}>
            <Grid container spacing={2} className="metrics-locations-filters">
              <Grid item xs={12} sm={4} lg={2}>
                <h6>DATES</h6>
                <Datepicker
                  select="range"
                  returnFormat="iso8601"
                  controls={["date"]}
                  value={state.filters.dates}
                  placeholder="__/__/____ - __/__/____"
                  onChange={(range) => handleUpdateFilters({
                    section: FiltersSections.Dates,
                    field: FiltersSections.Dates,
                    value: range.value,
                  })}
                  themeVariant="light"
                  inputProps={{
                    readOnly: true,
                    className: "dates-range-light-input",
                  }}
                />
              </Grid>

              {[
                FiltersSections.Departure,
                FiltersSections.Arrival,
              ].map(section =>
                <Grid key={section} item xs={12} sm={4} lg={3}>
                  <h6>{section.toUpperCase()} FILTERS</h6>

                  <div className="fields-container">
                    <Select
                      isClearable
                      className="base-select"
                      classNamePrefix="select"
                      placeholder={`Select ${section} Category`}
                      onChange={(option) => handleUpdateFilters({
                        section,
                        field: "category",
                        value: option?.value ?? null,
                      })}
                      value={categoryOptions.find(({ value }) => value === state.filters[section].category) ?? null}
                      options={categoryOptions}
                    >

                    </Select>

                    {state.filters[section].category && dropdowns[state.filters[section].category] && (
                      <AsyncSelect
                        className="base-select"
                        classNamePrefix="select"
                        cacheOptions
                        isMulti
                        loadOptions={loadDropdownOptions(state.filters[section].category)}
                        placeholder={dropdowns[state.filters[section].category].placeholder}
                        onChange={(value) => handleUpdateFilters({
                          section,
                          field: state.filters[section].category,
                          value,
                        })}
                        value={state.filters[section][state.filters[section].category] ?? []}
                        defaultOptions={dropdowns[state.filters[section].category].showDefaultOptions ? state.defaultOptions[state.filters[section].category] : []}
                        noOptionsMessage={() => !dropdowns[state.filters[section].category].showDefaultOptions ? "Type to search..." : "No options"}
                      />
                    )}

                    {state.filters[section].category === "airport" && (
                      <AsyncSelect
                        className="base-select"
                        classNamePrefix="select"
                        cacheOptions
                        isMulti
                        loadOptions={loadAirportOptions}
                        placeholder="Select Airport"
                        onChange={(value) => handleUpdateFilters({ section, field: "airport", value })}
                        value={state.filters[section].airport ?? []}
                        noOptionsMessage={() => "Type to search..."}
                      />
                    )}
                  </div>
                </Grid>,
              )}

              <Grid item xs={12} sm={8} lg={4}>
                <h6>COMBO FILTERS</h6>

                <div className="fields-container">
                  <Select
                    isClearable
                    className="base-select"
                    classNamePrefix="select"
                    placeholder={`Select combo Category`}
                    onChange={(option) => handleUpdateFilters({
                      section: FiltersSections.Combo,
                      field: "category",
                      value: option?.value ?? null,
                    })}
                    value={categoryOptions.find(({ value }) => value === state.filters[FiltersSections.Combo].category) ?? null}
                    options={categoryOptions}
                  >

                  </Select>

                  {state.filters[FiltersSections.Combo].category && dropdowns[state.filters[FiltersSections.Combo].category] && (
                    <>
                      <AsyncSelect
                        className="base-select combo-select"
                        classNamePrefix="select"
                        cacheOptions
                        loadOptions={loadDropdownOptions(state.filters[FiltersSections.Combo].category)}
                        placeholder={dropdowns[state.filters[FiltersSections.Combo].category].placeholder.replace("Select", "From")}
                        onChange={(value) => handleUpdateFilters({
                          section: FiltersSections.Combo,
                          field: `${state.filters[FiltersSections.Combo].category}From`,
                          value,
                        })}
                        value={state.filters[FiltersSections.Combo][`${state.filters[FiltersSections.Combo].category}From`] ?? null}
                        defaultOptions={dropdowns[state.filters[FiltersSections.Combo].category].showDefaultOptions ? state.defaultOptions[state.filters[FiltersSections.Combo].category] : []}
                        noOptionsMessage={() => !dropdowns[state.filters[FiltersSections.Combo].category].showDefaultOptions ? "Type to search..." : "No options"}
                      />

                      <AsyncSelect
                        className="base-select combo-select"
                        classNamePrefix="select"
                        cacheOptions
                        loadOptions={loadDropdownOptions(state.filters[FiltersSections.Combo].category)}
                        placeholder={dropdowns[state.filters[FiltersSections.Combo].category].placeholder.replace("Select", "To")}
                        onChange={(value) => handleUpdateFilters({
                          section: FiltersSections.Combo,
                          field: `${state.filters[FiltersSections.Combo].category}To`,
                          value,
                        })}
                        value={state.filters[FiltersSections.Combo][`${state.filters[FiltersSections.Combo].category}To`] ?? null}
                        defaultOptions={dropdowns[state.filters[FiltersSections.Combo].category].showDefaultOptions ? state.defaultOptions[state.filters[FiltersSections.Combo].category] : []}
                        noOptionsMessage={() => !dropdowns[state.filters[FiltersSections.Combo].category].showDefaultOptions ? "Type to search..." : "No options"}
                      />

                      <button
                        className="base-button icon-button icon-button-small evo-dark-green"
                        title="Add Combo filters"
                        onClick={handleAddComboFilter}
                        disabled={!state.filters[FiltersSections.Combo][`${state.filters[FiltersSections.Combo].category}From`] || !state.filters[FiltersSections.Combo][`${state.filters[FiltersSections.Combo].category}To`]}
                      >
                        <span className="material-icons">add_circle</span>
                      </button>
                    </>
                  )}

                  {state.filters[FiltersSections.Combo].category === "airport" && (
                    <>
                      <AsyncSelect
                        className="base-select combo-select"
                        classNamePrefix="select"
                        cacheOptions
                        loadOptions={loadAirportOptions}
                        placeholder="From Airport"
                        onChange={(value) => handleUpdateFilters({
                          section: FiltersSections.Combo,
                          field: "airportFrom",
                          value,
                        })}
                        value={state.filters[FiltersSections.Combo].airportFrom ?? []}
                        noOptionsMessage={() => "Type to search..."}
                      />

                      <AsyncSelect
                        className="base-select combo-select"
                        classNamePrefix="select"
                        cacheOptions
                        loadOptions={loadAirportOptions}
                        placeholder="To Airport"
                        onChange={(value) => handleUpdateFilters({
                          section: FiltersSections.Combo,
                          field: "airportTo",
                          value,
                        })}
                        value={state.filters[FiltersSections.Combo].airportTo ?? []}
                        noOptionsMessage={() => "Type to search..."}
                      />

                      <button
                        className="base-button icon-button icon-button-small evo-dark-green"
                        title="Add Combo filters"
                        onClick={handleAddComboFilter}
                        disabled={!state.filters[FiltersSections.Combo].airportFrom || !state.filters[FiltersSections.Combo].airportTo}
                      >
                        <span className="material-icons">add_circle</span>
                      </button>
                    </>
                  )}

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {state.filters[FiltersSections.Combo].values.map(([from, to], index) => (
                      <Chip
                        key={`${from.value}-${to.value}`}
                        label={`${from.code || from.label} - ${to.code || to.label}`}
                        onDelete={() => handleDeleteComboValue(index)}/>
                    ))}
                  </Box>
                </div>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <LoadingButton
                className="base-button evo-blue"
                onClick={handleApplyFilters}
              >
                Apply
              </LoadingButton>
            </Box>
          </Box>
        )}
    </>
  );
};
